"use client"

import { useEffect, useRef, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Bot } from "lucide-react"
import { useLanguage } from "@/hooks/use-language"

export function Navbar({ 
  onAiContextClick,
  onContactClick,
  onPortfolioClick
}: { 
  onAiContextClick?: () => void,
  onContactClick?: () => void,
  onPortfolioClick?: () => void
}) {
  const { t } = useLanguage()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const lastScrollYRef = useRef(0)
  const isMenuOpenRef = useRef(isMenuOpen)

  useEffect(() => {
    isMenuOpenRef.current = isMenuOpen
  }, [isMenuOpen])

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const previousScrollY = lastScrollYRef.current
      
      setIsScrolled(currentScrollY > 20)

      if (currentScrollY < 10) {
        setIsVisible(true)
      } else if (currentScrollY > previousScrollY) {
        if (!isMenuOpenRef.current) setIsVisible(false)
      } else {
        setIsVisible(true)
      }

      lastScrollYRef.current = currentScrollY
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const scrollToSection = (href: string) => {
    setIsMenuOpen(false)
    const element = document.querySelector(href)
    if (element) {
      const offset = 80
      const bodyRect = document.body.getBoundingClientRect().top
      const elementRect = element.getBoundingClientRect().top
      const elementPosition = elementRect - bodyRect
      const offsetPosition = elementPosition - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      })
    }
  }

  const navLinks = [
    { label: t.nav.about, href: "#about" },
    { label: t.nav.experience, href: "#works" },
    { label: t.nav.portfolioLabel.split(" // ")[0], href: "#portfolio" },
    { label: t.nav.stack, href: "#stack" },
  ]

  return (
    <>
      <AnimatePresence>
        {(isScrolled && isVisible) && (
          <div className="fixed top-4 md:top-6 left-0 right-0 z-50 flex justify-center px-3 md:px-4 pointer-events-none">
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
              className="pointer-events-auto"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-background/45 backdrop-blur-xl rounded-[1.75rem] md:rounded-full border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.3)]" />
                
                <div className="relative flex items-center h-14 md:h-12 px-2 md:px-1.5 gap-1.5 md:gap-1">
                  <button
                    onClick={() => setIsMenuOpen((open) => !open)}
                    className="flex items-center justify-center w-11 h-11 md:w-10 md:h-10 rounded-full hover:bg-white/5 active:scale-95 transition-[background-color,transform] duration-150 group/menu"
                    aria-label="Toggle Menu"
                  >
                    <div className="flex flex-col gap-[5px] md:gap-1 w-[18px] md:w-4">
                      <motion.span 
                        animate={isMenuOpen ? { rotate: 45, y: 5 } : { rotate: 0, y: 0 }}
                        className="w-full h-[2px] md:h-0.5 bg-foreground rounded-full origin-center" 
                      />
                      <motion.span 
                        animate={isMenuOpen ? { rotate: -45, y: -1 } : { rotate: 0, y: 0 }}
                        className="w-full h-[2px] md:h-0.5 bg-foreground rounded-full origin-center" 
                      />
                    </div>
                  </button>

                  <button 
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    className="px-3.5 py-2.5 md:px-3 md:py-2 hover:opacity-70 transition-opacity"
                  >
                    <span className="font-mono text-[11px] md:text-[10px] tracking-[0.24em] md:tracking-[0.2em] uppercase whitespace-nowrap">Diego Nunez</span>
                  </button>

                  {/* AI Context Tool Link */}
                  <button
                    onClick={onAiContextClick}
                    className="hidden md:flex items-center px-3 py-2 hover:opacity-70 transition-opacity gap-2 group/ai"
                  >
                    <Bot className="w-3.5 h-3.5 text-muted-foreground group-hover/ai:text-emerald-500 transition-colors" />
                    <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-muted-foreground group-hover/ai:text-emerald-500 transition-colors">
                      {t.nav.aiContext}
                    </span>
                  </button>

                  <button
                    onClick={() => {
                      if (onContactClick) onContactClick()
                      else scrollToSection("#contact")
                    }}
                    className="relative flex items-center h-11 md:h-9 pl-4 pr-2 md:pr-1.5 gap-2.5 md:gap-2 bg-white text-black rounded-full overflow-hidden group/contact active:scale-95 transition-transform shadow-[0_10px_30px_rgba(255,255,255,0.08)]"
                  >
                    <span className="font-mono text-[10px] md:text-[9px] font-bold tracking-[0.28em] md:tracking-widest uppercase">
                      {t.nav.contact}
                    </span>
                    <div className="w-7 h-7 md:w-6 md:h-6 bg-black rounded-full flex items-center justify-center text-white transition-transform duration-500 group-hover/contact:rotate-45">
                      <svg width="11" height="11" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2.5 2.5H9.5V9.5M9.5 2.5L2.5 9.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMenuOpen(false)}
              className="fixed inset-0 z-40 bg-background/20 backdrop-blur-sm"
            />
            <motion.div
              initial={{ y: -10, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: -10, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="fixed top-[4.75rem] md:top-20 left-1/2 -translate-x-1/2 z-50 w-[calc(100vw-1.5rem)] max-w-md md:w-full md:max-w-[280px]"
            >
              <div className="bg-background/85 backdrop-blur-2xl rounded-[2.25rem] md:rounded-[2rem] border border-white/10 shadow-[0_24px_80px_rgba(0,0,0,0.45)] overflow-hidden p-5 md:p-4">
                <nav className="flex flex-col gap-2 md:gap-1">
                  {navLinks.map((link, index) => (
                    <button
                      key={link.label}
                      onClick={() => {
                        if (link.href === "#portfolio" && onPortfolioClick) {
                          setIsMenuOpen(false)
                          onPortfolioClick()
                        } else {
                          scrollToSection(link.href)
                        }
                      }}
                      className="flex items-center justify-between w-full p-5 md:p-4 rounded-[1.35rem] md:rounded-xl hover:bg-white/5 active:scale-[0.985] transition-[background-color,transform] duration-150 group/item text-left"
                    >
                      <span className="font-sans text-[1.75rem] leading-none md:text-xl tracking-[-0.04em] text-foreground/80 group-hover/item:text-foreground transition-colors">
                        {link.label}
                      </span>
                      <span className="font-mono text-[10px] md:text-[9px] tracking-[0.35em] uppercase text-muted-foreground">0{index + 1}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}
